import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload, Trash2, ArrowLeft, ArrowRight, Loader2, CheckCircle2,
  AlertCircle, LogOut, Lock, GripVertical, Plus,
} from 'lucide-react';

// Photo manager for the salon owner. Everything she uploads is resized and
// cropped in the browser before it is sent, so a 12MP phone photo never
// reaches the server and never slows the homepage down.
//
// Shapes are fixed on purpose. The top photo sits inside a blob mask and the
// gallery is a fixed-height ribbon, so free-form images would break both.

// Long-edge caps, not crop boxes. Photos keep their own shape and nothing is
// trimmed - the owner should never have to pre-crop a photo to use it.
const HERO = { max: 1600 };
const TILE = { max: 1400 };
const MAX_HERO = 8;
const MAX_GALLERY = 40;

const api = (path, opts = {}) =>
  fetch(`/api/photos${path}`, { credentials: 'same-origin', ...opts });

/** Shrink the photo in the browser and hand back a small file.
 *  Nothing is cropped - only the long edge is capped. */
function processImage(file, box) {
  const encode = (canvas, type, quality) =>
    new Promise((res) => canvas.toBlob(res, type, quality));

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('That does not look like a photo.'));
      img.onload = async () => {
        // Shrink to fit the cap, keeping the whole picture and its own shape.
        // A photo that is already small is left at its own size.
        const scale = Math.min(1, box.max / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        // A browser that cannot encode webp does not fail - it silently
        // returns PNG, which for a photo is several times larger than the
        // JPEG we would rather send. So check what actually came back.
        let blob = await encode(canvas, 'image/webp', 0.82);
        if (!blob || blob.type !== 'image/webp') {
          blob = await encode(canvas, 'image/jpeg', 0.82);
        }
        if (!blob || !/^image\/(webp|jpeg)$/.test(blob.type)) {
          return reject(new Error('That photo could not be prepared. Try a JPG.'));
        }
        resolve(blob);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function Banner({ kind, children }) {
  if (!children) return null;
  const bad = kind === 'error';
  return (
    <div
      role="status"
      className={`flex items-start gap-2.5 rounded-2xl px-4 py-3 text-sm font-semibold ${
        bad ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
      }`}
    >
      {bad ? <AlertCircle className="w-5 h-5 shrink-0" /> : <CheckCircle2 className="w-5 h-5 shrink-0" />}
      <span>{children}</span>
    </div>
  );
}

function DropZone({ onFiles, busy, multiple, children }) {
  const inputRef = useRef(null);
  const [over, setOver] = useState(false);

  const pick = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    if (list.length) onFiles(list);
  };

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files); }}
      onClick={() => !busy && inputRef.current?.click()}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) inputRef.current?.click(); }}
      role="button"
      tabIndex={0}
      className={`cursor-pointer rounded-3xl border-2 border-dashed px-6 py-10 text-center transition-colors focus:outline-none focus:ring-4 focus:ring-pink-200 ${
        over ? 'border-pink-400 bg-pink-50' : 'border-pink-200 bg-white hover:bg-pink-50/60'
      } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(e) => { pick(e.target.files); e.target.value = ''; }}
      />
      {busy ? (
        <span className="inline-flex items-center gap-2 font-bold text-pink-600">
          <Loader2 className="w-5 h-5 animate-spin" /> Preparing your photos...
        </span>
      ) : (
        children
      )}
    </div>
  );
}


/** One photo tile. Compact on purpose: with two dozen photos she needs to see
 *  the whole order at once, not scroll through it. The position is on the
 *  image itself so it stays readable at this size. */
function PhotoCard({ item, index, total, onAlt, onMove, onRemove, drag }) {
  return (
    <li
      onDragOver={(e) => { e.preventDefault(); drag.over(index); }}
      onDrop={(e) => { e.preventDefault(); drag.drop(index); }}
      className={`group bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${
        drag.overIndex === index && drag.fromIndex !== index
          ? 'border-pink-400 ring-4 ring-pink-100'
          : 'border-pink-100'
      } ${drag.fromIndex === index ? 'opacity-40' : ''}`}
    >
      <div
        draggable
        onDragStart={() => drag.start(index)}
        onDragEnd={drag.end}
        title="Drag to move this photo"
        className="relative cursor-grab active:cursor-grabbing"
      >
        <img src={`/api/photos/img/${item.id}`} alt="" className="w-full h-28 object-contain bg-pink-50/70" />
        <span className="absolute top-1.5 left-1.5 w-8 h-8 grid place-items-center rounded-full bg-white/95 shadow font-display text-base font-black text-ink-900">
          {index + 1}
        </span>
        <span className="absolute bottom-1.5 right-1.5 rounded-full bg-black/60 text-white text-[11px] font-bold px-2 py-0.5">
          {index + 1} of {total}
        </span>
        <span className="absolute inset-0 grid place-items-center bg-black/45 text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          <GripVertical className="w-5 h-5" />
        </span>
      </div>

      <div className="p-2 space-y-1.5">
        <input
          value={item.alt}
          onChange={(e) => onAlt(e.target.value)}
          placeholder="Describe this photo"
          className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-pink-200"
        />
        <div className="flex items-center">
          <button onClick={() => onMove(-1)} disabled={index === 0} aria-label={`Move photo ${index + 1} earlier`}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-pink-50 hover:text-pink-600 disabled:opacity-25">
            <ArrowLeft className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onMove(1)} disabled={index === total - 1} aria-label={`Move photo ${index + 1} later`}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-pink-50 hover:text-pink-600 disabled:opacity-25">
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
          <button onClick={onRemove} aria-label={`Remove photo ${index + 1}`}
            className="ml-auto p-1.5 rounded-lg text-rose-400 hover:text-rose-600 hover:bg-rose-50">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </li>
  );
}

/** "Add photos" sits as the last tile in the grid. With a full gallery the
 *  drop zone at the top is scrolled out of sight, and she should never have to
 *  hunt for the way to add another photo. */
function AddTile({ onFiles, busy, full, max, room }) {
  const inputRef = useRef(null);
  const pick = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith('image/'));
    if (list.length) onFiles(list);
  };

  if (full) {
    return (
      <li className="rounded-2xl border-2 border-dashed border-gray-200 grid place-items-center p-4 text-center min-h-[11rem]">
        <p className="text-xs font-bold text-gray-400">
          Full at {max} photos.<br />Remove one to add another.
        </p>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={() => !busy && inputRef.current?.click()}
        disabled={busy}
        className="w-full h-full min-h-[11rem] rounded-2xl border-2 border-dashed border-pink-300 bg-white hover:bg-pink-50 hover:border-pink-400 transition-colors grid place-items-center p-4 text-center focus:outline-none focus:ring-4 focus:ring-pink-200 disabled:opacity-50"
      >
        <span>
          {busy ? (
            <Loader2 className="w-7 h-7 mx-auto text-pink-500 animate-spin" />
          ) : (
            <Plus className="w-7 h-7 mx-auto text-pink-500" />
          )}
          <span className="mt-2 block font-extrabold text-ink-900 text-sm">Add photos</span>
          <span className="mt-0.5 block text-xs text-gray-500">Room for {room} more</span>
        </span>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => { pick(e.target.files); e.target.value = ''; }}
      />
    </li>
  );
}

/** A whole managed section: drop zone, then the numbered photos. */
function PhotoSection({ title, blurb, items, max, shape, busy, onAdd, onChange, emptyNote }) {
  const [fromIndex, setFrom] = useState(null);
  const [overIndex, setOver] = useState(null);

  const move = (from, to) => {
    if (from === null || to === null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const drag = {
    fromIndex,
    overIndex,
    start: setFrom,
    over: setOver,
    end: () => { setFrom(null); setOver(null); },
    drop: (to) => { move(fromIndex, to); setFrom(null); setOver(null); },
  };

  return (
    <section>
      <h2 className="font-display text-2xl font-black text-ink-900">{title}</h2>
      <p className="mt-1 text-sm text-gray-600">{blurb}</p>
      <p className="mt-1 text-sm font-bold text-pink-600">
        {items.length === 0
          ? emptyNote
          : `${items.length} photo${items.length === 1 ? '' : 's'}, shown in this order. Drag any photo to move it.`}
      </p>

      {items.length === 0 && (
        <div className="mt-4">
          <DropZone onFiles={onAdd} busy={busy} multiple>
            <Upload className="w-6 h-6 mx-auto text-pink-400" />
            <p className="mt-2 font-extrabold text-ink-900">Drag photos here, or click to choose</p>
            <p className="mt-1 text-sm text-gray-500">
              Several at once is fine. Whole photos, nothing cropped.
            </p>
          </DropZone>
        </div>
      )}

      {items.length > 0 && (
        <ul className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((item, i) => (
            <PhotoCard
              key={item.id}
              item={item}
              index={i}
              total={items.length}
              drag={drag}
              onAlt={(alt) => onChange(items.map((x, k) => (k === i ? { ...x, alt } : x)))}
              onMove={(dir) => move(i, i + dir)}
              onRemove={() => onChange(items.filter((_, k) => k !== i))}
            />
          ))}
          <AddTile onFiles={onAdd} busy={busy} full={items.length >= max} max={max} room={max - items.length} />
        </ul>
      )}
    </section>
  );
}

function Login({ onIn }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await api('/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) return onIn();
      setError(data.error || 'That did not work. Try again.');
    } catch {
      setError('Could not reach the site. Check your internet and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-white to-cyan-50 px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-[2rem] shadow-xl shadow-pink-100 border border-pink-100 p-8">
        <div className="flex items-center gap-2 text-pink-500">
          <Lock className="w-5 h-5" />
          <span className="text-xs font-extrabold uppercase tracking-[0.2em]">Photo manager</span>
        </div>
        <h1 className="mt-3 font-display text-3xl font-black text-ink-900">Sign in</h1>
        <p className="mt-2 text-sm text-gray-600">Enter the password for the Bubbles &amp; Bark website.</p>

        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          className="mt-6 w-full rounded-2xl border border-gray-200 px-4 py-3 text-base focus:outline-none focus:ring-4 focus:ring-pink-200 focus:border-pink-300"
          placeholder="Password"
        />

        {error && <p className="mt-3 text-sm font-semibold text-rose-600">{error}</p>}

        <button
          type="submit"
          disabled={busy || !password}
          className="mt-5 w-full rounded-full bg-gradient-to-r from-pink-500 to-cyan-500 text-white font-extrabold py-3.5 shadow-lg shadow-pink-200 disabled:opacity-50"
        >
          {busy ? 'Checking...' : 'Sign in'}
        </button>

        <Link to="/" className="mt-5 block text-center text-sm font-semibold text-gray-500 hover:text-pink-600">
          Back to the website
        </Link>
      </form>
    </div>
  );
}

export default function Admin() {
  const [ready, setReady] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [draft, setDraft] = useState({ hero: [], gallery: [] });
  const [published, setPublished] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const loadDraft = useCallback(async () => {
    const res = await api('/draft');
    if (res.status === 401) { setAuthed(false); return; }
    const data = await res.json();
    setDraft(data.draft);
    setPublished(data.published);
    setAuthed(true);
  }, []);

  useEffect(() => {
    api('/session')
      .then((r) => r.json())
      .then((d) => (d.authed ? loadDraft() : null))
      .catch(() => {})
      .finally(() => setReady(true));
  }, [loadDraft]);

  const save = async (next) => {
    setDraft(next);
    setError('');
    setNote('');
    try {
      const res = await api('/draft', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(next),
      });
      if (res.status === 401) { setAuthed(false); return; }
      const data = await res.json();
      setDraft(data.draft);
      setPublished(data.published);
    } catch {
      setError('Could not save that change. Check your internet.');
    }
  };

  const upload = async (file, box) => {
    const blob = await processImage(file, box);
    const res = await api('/upload', {
      method: 'POST',
      headers: { 'Content-Type': blob.type },
      body: blob,
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.error || 'That photo could not be uploaded.');
    }
    return (await res.json()).id;
  };

  /** Shared by both sections: prepare each file, then append. */
  const addTo = (key, box, max) => async (files) => {
    setBusy(true); setError(''); setNote('');
    const current = draft[key];
    const room = max - current.length;
    if (room <= 0) {
      setBusy(false);
      setError(`That section is full at ${max} photos. Remove one first.`);
      return;
    }
    try {
      const added = [];
      for (const file of files.slice(0, room)) {
        added.push({ id: await upload(file, box), alt: '' });
      }
      await save({ ...draft, [key]: [...current, ...added] });
      if (files.length > room) setError(`Only ${room} more would fit, so the rest were skipped.`);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true); setError(''); setNote('');
    try {
      const res = await api('/publish', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Could not publish.'); return; }
      setPublished(true);
      setNote('Published. Your website is updated.');
    } catch {
      setError('Could not publish. Check your internet and try again.');
    } finally { setBusy(false); }
  };

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-pink-50">
        <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }
  if (!authed) return <Login onIn={loadDraft} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/70 to-white">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-pink-100">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-xl sm:text-2xl font-black text-ink-900 truncate">Website photos</h1>
            <p className="text-xs text-gray-500">
              {published ? 'Everything here is live on the website.' : 'You have changes that are not live yet.'}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={publish}
              disabled={busy || published}
              className="rounded-full bg-gradient-to-r from-pink-500 to-cyan-500 text-white font-extrabold px-5 sm:px-7 py-3 shadow-lg shadow-pink-200 disabled:opacity-40 disabled:shadow-none"
            >
              {busy ? 'Working...' : published ? 'Published' : 'Publish'}
            </button>
            <button
              onClick={() => api('/logout', { method: 'POST' }).then(() => setAuthed(false))}
              aria-label="Sign out"
              className="p-3 rounded-full text-gray-400 hover:text-pink-600 hover:bg-pink-50"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-12">
        <div className="space-y-3">
          <Banner kind="error">{error}</Banner>
          <Banner kind="ok">{note}</Banner>
          {!published && !error && !note && (
            <Banner kind="ok">Changes are saved. Press Publish when you want them on the website.</Banner>
          )}
        </div>

        <PhotoSection
          title="Photos at the top of the page"
          blurb="The big photo people see first. Add more than one and they fade from one to the next, in the order below. Pick photos where the pet fills the frame, because the edges are trimmed into a round shape."
          emptyNote="No photos added yet, so the website is showing the original one."
          items={draft.hero}
          max={MAX_HERO}
          shape={HERO.label}
          busy={busy}
          onAdd={addTo('hero', HERO, MAX_HERO)}
          onChange={(hero) => save({ ...draft, hero })}
        />

        <PhotoSection
          title="The moving photo strip"
          blurb={'The row of photos that slides across under "Happy clients, fabulous fur". Four or more looks best.'}
          emptyNote="No photos added yet, so the website is showing the original ones."
          items={draft.gallery}
          max={MAX_GALLERY}
          shape={TILE.label}
          busy={busy}
          onAdd={addTo('gallery', TILE, MAX_GALLERY)}
          onChange={(gallery) => save({ ...draft, gallery })}
        />

        <footer className="pt-4 pb-12 border-t border-pink-100 flex flex-wrap items-center gap-4">
          <Link to="/" className="text-sm font-bold text-gray-500 hover:text-pink-600">View the website</Link>
          <button
            onClick={publish}
            disabled={busy || published}
            className="ml-auto rounded-full bg-gradient-to-r from-pink-500 to-cyan-500 text-white font-extrabold px-8 py-3.5 shadow-lg shadow-pink-200 disabled:opacity-40 disabled:shadow-none"
          >
            {published ? 'Everything is published' : 'Publish to the website'}
          </button>
        </footer>
      </main>
    </div>
  );
}
